import { Badge, BlockStack, Box, Button, Card, Icon, IndexTable, InlineStack, Link, Text, useIndexResourceState } from '@shopify/polaris';
import SimpleFilter from 'components/simpleFilter';
// import { useGetTrip_manifest_invoices, TypedTrip_manifest_invoice } from 'queries/trip_manifest_invoice.query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BillFilledIcon, CashDollarIcon, DeleteIcon, ExportIcon, InfoIcon, TaxFilledIcon } from '@shopify/polaris-icons';

import helpers from 'helpers/index';
import Pagination from 'components/pagination';
import dateandtime from 'date-and-time';
import { useDeleteConfirm } from 'context/DeleteConfirmContext';
import { useNotification } from 'context/NotificationContext';
import __ from 'languages/index';
import { useMutateDeleteInvoices, useMutateSignInvoices, useGetInvoices, TypedInvoices } from 'queries/invoices.query';

export default function InvoiceDraft() {
  const rootSearchPath = window.location.search;
  let StringQuery: any = helpers.ExtractUrl(rootSearchPath) || false;

  const initialQuery = useMemo(
    () => ({
      query: '',
      page: 1,
      limit: 200,
      sort: 'createdAt:desc',
      is_signed: 0,
    }),
    []
  );

  const [mainQuery, setMainQuery] = useState<any>({
    ...initialQuery,
    ...StringQuery,
  });

  const { refetch: getEntities, data, isFetching: loading } = useGetInvoices(mainQuery);
  const { addNotification } = useNotification();
  const { showDeleteConfirm } = useDeleteConfirm();

  const [entities, setEntities] = useState<TypedInvoices[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [sum, setSum] = useState({ count: 0, TgThTien: 0, TgTThue: 0, TgTTTBSo: 0 });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (data) {
      let { body, totalItems } = data;
      let { records, sum } = body;
      setEntities(records);
      setTotalItems(totalItems);
      setSum(sum);
    }
  }, [data]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 800px)');
    const handleChange = (event: MediaQueryListEvent | MediaQueryList) => {
      const matches = 'matches' in event ? event.matches : mediaQuery.matches;
      setIsMobile(matches);
    };

    handleChange(mediaQuery);
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange as (event: MediaQueryListEvent) => void);
    } else {
      mediaQuery.addListener(handleChange as (event: MediaQueryListEvent) => void);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange as (event: MediaQueryListEvent) => void);
      } else {
        mediaQuery.removeListener(handleChange as (event: MediaQueryListEvent) => void);
      }
    };
  }, []);

  const onChangePagination = useCallback((numPage: number, limit: number) => {
    setMainQuery((prevMainQuery) => ({ ...prevMainQuery, page: numPage, limit: limit }));
  }, []);

  useEffect(() => {
    let buildURLSearch = helpers.buildEndUrl(mainQuery);
    if (window.location.search !== buildURLSearch) {
      window.history.replaceState(null, 'Dashboard', '/draft' + buildURLSearch);
    }
  }, [mainQuery]);

  /** xóa hóa đơn */
  const { mutateAsync: deleteInvoiceMutation } = useMutateDeleteInvoices();
  const [currentDeletingInvoice, setCurrentDeletingInvoice] = useState<string | null>(null);
  const deleteInvoiceCallback = useCallback(
    async (invoiceId?: string) => {
      if (!invoiceId) return;
      const confirmed = await showDeleteConfirm(
        'Xóa hóa đơn này?',
        'BẠN ĐANG XÓA HÓA ĐƠN! Chỉ có hóa đơn nháp mới được phép xóa! Hành động này không thể khôi phục. '
      );
      if (!confirmed) return;
      setCurrentDeletingInvoice(invoiceId);
      try {
        await deleteInvoiceMutation(String(invoiceId) as any);
        addNotification('info', 'Xóa hóa đơn thành công!');
      } catch (e) {
        addNotification('error', 'Không thể xóa hóa đơn này!');
      } finally {
        setCurrentDeletingInvoice(null);
      }
    },
    [addNotification, deleteInvoiceMutation, showDeleteConfirm]
  );

  /** Ký & phát hành hóa đơn */
  const { mutateAsync: signInvoiceMutation } = useMutateSignInvoices();
  const [currentSignInvoice, setCurrentSignInvoice] = useState<string | null>(null);
  const signInvoiceCallback = useCallback(
    async (tripManifestInvoiceData: TypedInvoices) => {
      if (!tripManifestInvoiceData?.invoice_id) return;
      const confirmed = await showDeleteConfirm(
        'Ký hóa đơn này?',
        'BẠN ĐANG KÝ MỘT HÓA ĐƠN! Bạn nên nhớ, hành động này không thể hoàn tác, việc hoàn tác phải được LẬP BIÊN BẢN. HÃY KIỂM TRA KỸYYYYYYYYYYYYY',
        {
          customAction: {
            content: 'Xem lại',
            onAction: () => {
              if (tripManifestInvoiceData.urlDownloadPDF) {
                window.open(tripManifestInvoiceData.urlDownloadPDF, '_blank');
              }
            },
          },
        }
      );
      if (!confirmed) return;

      setCurrentSignInvoice(String(tripManifestInvoiceData.invoice_id));
      try {
        await signInvoiceMutation(String(tripManifestInvoiceData.invoice_id) as any);
        addNotification('info', 'Ký hóa đơn thành công!');
      } catch (e) {
        // addNotification('error', 'Không thể ký hóa đơn này!');
      } finally {
        setCurrentSignInvoice(null);
      }
    },
    [addNotification, showDeleteConfirm, signInvoiceMutation]
  );

  const filterCallback = useCallback(
    (value: any) => {
      if (value === false) {
        return setMainQuery(initialQuery);
      }
      setMainQuery((oldValue) => ({ ...oldValue, ...value }));
    },
    [initialQuery]
  );

  const resourceName = {
    singular: 'invoice',
    plural: 'invoices',
  };

  const resourceIDResolver = useCallback((tripManifestInvoiceData: TypedInvoices) => String(tripManifestInvoiceData.invoice_id), []);
  const { selectedResources, allResourcesSelected, handleSelectionChange, clearSelection, removeSelectedResources } = useIndexResourceState(
    (entities || []) as any,
    {
      resourceIDResolver,
    }
  );

  /** Chức năng ký hàng loạt ây eiii */
  const signedAllSelected = useCallback(async () => {
    for (let invoiceID of selectedResources) {
      setCurrentSignInvoice(String(invoiceID));
      try {
        await signInvoiceMutation(String(invoiceID) as any);
        removeSelectedResources([invoiceID]);
      } catch (e) {
      } finally {
        setCurrentSignInvoice(null);
      }
      await helpers.sleep(1000);
    }
    clearSelection();
    getEntities();
  }, [selectedResources, signInvoiceMutation]);

  /** Chức năng xóa bỏ hàng loạt ây eiii */
  const deleteAllSelected = useCallback(async () => {
    const confirmed = await showDeleteConfirm(
      `Xóa ${selectedResources?.length} hóa đơn?`,
      'BẠN ĐANG XÓA HÀNG LOẠT HÓA ĐƠN! Chỉ có hóa đơn nháp mới được phép xóa! Hành động này không thể khôi phục. '
    );
    if (!confirmed) return;

    for (let invoiceID of selectedResources) {
      setCurrentDeletingInvoice(invoiceID);
      try {
        await deleteInvoiceMutation(String(invoiceID) as any);
      } catch (e) {
      } finally {
        setCurrentDeletingInvoice(null);
        getEntities();
        removeSelectedResources([invoiceID]);
      }
      await helpers.sleep(1000);
    }
    clearSelection();
  }, [deleteInvoiceMutation, selectedResources, getEntities]);

  const rowMarkup = useMemo(
    () =>
      entities.map(({ trip_manifest_invoice, ...invoices }, index) => {
        const invoiceId = resourceIDResolver(invoices);
        const taxType = invoices.TaxType || 'DEFAULT';
        const journey = trip_manifest_invoice.trip_manifest?.trip?.journey;
        const journeyName = journey?.journey_name || `${journey?.journey_from || ''} - ${journey?.journey_to || ''}`.trim() || '--';
        const createdAt = invoices.createdAt ? dateandtime.format(new Date(Number(invoices.createdAt)), 'HH:mm DD/MM/YYYY') : '--';
        const pdfUrl = process.env.REACT_APP_BACKEND_URL + `/invoices/view_pdf/${invoices.maTraCuu}`;
        const departureDate = trip_manifest_invoice.trip_manifest?.departure_date;
        const datedy = departureDate ? dateandtime.format(new Date(Number(departureDate)), 'YYYY-MM-DD') : '';
        const tripTime = trip_manifest_invoice.trip_manifest?.departure_hour || '--';
        const journeyId = trip_manifest_invoice.trip_manifest?.trip?.journey?.id;
        const time_id = trip_manifest_invoice.trip_manifest?.time_id;
        const ticketManagersLink = `/ticket_managers?d=${datedy}&journey_id=${String(journeyId ?? '')}&htext=${tripTime}&h=${String(time_id ?? '')}`;

        return (
          <IndexTable.Row
            id={invoiceId}
            key={invoiceId}
            selected={selectedResources.includes(invoiceId)}
            position={index}
            tone={currentSignInvoice === trip_manifest_invoice.id ? 'warning' : 'success'}
          >
            <IndexTable.Cell>{index}</IndexTable.Cell>
            <IndexTable.Cell>
              <Link url={pdfUrl || '#'} target="_blank">
                <Text variant="bodyMd" fontWeight="bold" as="span">
                  {invoices.maTraCuu || '--'}
                </Text>
              </Link>
            </IndexTable.Cell>
            <IndexTable.Cell>{invoices.MTChieu || '--'}</IndexTable.Cell>
            <IndexTable.Cell>{trip_manifest_invoice?.trip_manifest?.vehicle?.license_plate || '--'}</IndexTable.Cell>
            <IndexTable.Cell>
              <Link removeUnderline url={ticketManagersLink}>
                <Text as="span" fontWeight="bold">
                  <Badge tone="success">{tripTime}</Badge> • {journeyName}
                </Text>
              </Link>
            </IndexTable.Cell>
            <IndexTable.Cell>
              {trip_manifest_invoice.trip_manifest?.departure_date
                ? dateandtime.format(new Date(Number(trip_manifest_invoice.trip_manifest?.departure_date)), 'DD-MM-YYYY')
                : '--'}
            </IndexTable.Cell>
            <IndexTable.Cell>
              <Badge size="small" tone={taxType === 'COMPANY' ? 'critical' : 'info'}>
                {__('tax_type_' + taxType)}
              </Badge>
            </IndexTable.Cell>
            <IndexTable.Cell>{invoices.NMua_HVTNMHang || '--'}</IndexTable.Cell>
            <IndexTable.Cell>{invoices.NMua_CCCDan || '--'}</IndexTable.Cell>
            <IndexTable.Cell>{invoices.NMua_Ten || '--'}</IndexTable.Cell>
            <IndexTable.Cell>{invoices.NMua_MST || '--'}</IndexTable.Cell>
            <IndexTable.Cell>{invoices.NMua_SDThoai || '--'}</IndexTable.Cell>
            <IndexTable.Cell>{invoices.NMua_DCTDTu || '--'}</IndexTable.Cell>
            <IndexTable.Cell>{invoices.NMua_DChi || '--'}</IndexTable.Cell>
            <IndexTable.Cell>
              <Text as="span" alignment="end" numeric>
                {helpers.formatNumberWithCommas(invoices.TgTThue)}
              </Text>
            </IndexTable.Cell>
            <IndexTable.Cell>
              <Text as="span" alignment="end" numeric>
                {helpers.formatNumberWithCommas(invoices.TgThTien)}
              </Text>
            </IndexTable.Cell>
            <IndexTable.Cell>
              <Text as="span" alignment="end" numeric>
                {helpers.formatNumberWithCommas(invoices.TgTTTBSo)}
              </Text>
            </IndexTable.Cell>
            <IndexTable.Cell>{createdAt}</IndexTable.Cell>
            <IndexTable.Cell>{invoices?.author_data?.display_name || '--'}</IndexTable.Cell>
            <IndexTable.Cell>
              <InlineStack wrap={false} gap="200">
                <Button
                  icon={ExportIcon}
                  variant="monochromePlain"
                  loading={currentSignInvoice === invoices.invoice_id}
                  onClick={() => {
                    return signInvoiceCallback(invoices);
                  }}
                ></Button>

                <Button
                  icon={DeleteIcon}
                  variant="monochromePlain"
                  loading={currentDeletingInvoice === invoices.invoice_id}
                  onClick={() => {
                    return deleteInvoiceCallback(invoices.invoice_id);
                  }}
                ></Button>
              </InlineStack>
            </IndexTable.Cell>
          </IndexTable.Row>
        );
      }),
    [entities, selectedResources, resourceIDResolver, currentSignInvoice, signInvoiceCallback, currentDeletingInvoice, deleteInvoiceCallback]
  );

  return (
    <BlockStack gap="400">
      <BlockStack gap="200">
        <InlineStack gap={isMobile ? '200' : '400'} wrap>
          <Box minWidth={isMobile ? '100%' : '220px'} width={isMobile ? '100%' : undefined}>
            <Card padding="300">
              <BlockStack gap="100">
                <InlineStack gap="200" blockAlign="center" align="start" wrap={false}>
                  <Box as="span">
                    <Icon source={InfoIcon} tone="subdued" />
                  </Box>
                  <Text as="span" tone="subdued">
                    Tổng số dữ liệu
                  </Text>
                </InlineStack>
                <Text variant="headingLg" as="p">
                  {helpers.formatNumberWithCommas(sum?.count || 0)}
                </Text>
              </BlockStack>
            </Card>
          </Box>
          <Box minWidth={isMobile ? '100%' : '220px'} width={isMobile ? '100%' : undefined}>
            <Card padding="300">
              <BlockStack gap="100">
                <InlineStack gap="200" blockAlign="center" align="start" wrap={false}>
                  <Box as="span">
                    <Icon source={CashDollarIcon} tone="subdued" />
                  </Box>
                  <Text as="span" tone="subdued">
                    Tổng tiền
                  </Text>
                </InlineStack>
                <Text variant="headingLg" as="p">
                  {helpers.formatNumberWithCommas(sum?.TgTTTBSo || 0)}
                </Text>
              </BlockStack>
            </Card>
          </Box>
          <Box minWidth={isMobile ? '100%' : '220px'} width={isMobile ? '100%' : undefined}>
            <Card padding="300">
              <BlockStack gap="100">
                <InlineStack gap="200" blockAlign="center" align="start" wrap={false}>
                  <Box as="span">
                    <Icon source={TaxFilledIcon} tone="subdued" />
                  </Box>
                  <Text as="span" tone="subdued">
                    Tổng thuế
                  </Text>
                </InlineStack>
                <Text variant="headingLg" as="p">
                  {helpers.formatNumberWithCommas(sum?.TgTThue || 0)}
                </Text>
              </BlockStack>
            </Card>
          </Box>
          <Box minWidth={isMobile ? '100%' : '220px'} width={isMobile ? '100%' : undefined}>
            <Card padding="300">
              <BlockStack gap="100">
                <InlineStack gap="200" blockAlign="center" align="start" wrap={false}>
                  <Box as="span">
                    <Icon source={BillFilledIcon} tone="subdued" />
                  </Box>
                  <Text as="span" tone="subdued">
                    Tổng thành tiền
                  </Text>
                </InlineStack>
                <Text variant="headingLg" as="p">
                  {helpers.formatNumberWithCommas(sum?.TgThTien || 0)}
                </Text>
              </BlockStack>
            </Card>
          </Box>
        </InlineStack>
      </BlockStack>

      <Box paddingBlockEnd="400">
        <Card padding="0">
          <div className="invoice_managers_wrapper">
            <SimpleFilter
              loading={loading}
              onCallback={filterCallback}
              options={[
                { label: 'Công ty', value: 'COMPANY', field: 'TaxType' },
                { label: 'Cá nhân', value: 'PERSONAL', field: 'TaxType' },
              ]}
              sortField={[
                { label: 'Tổng tiền', field: 'TgTTTBSo' },
                { label: 'Ngày ký', field: 'signedAt' },
              ]}
              // defaultSort="trip_manifest.departure_date:desc"
            />
            <IndexTable
              resourceName={resourceName}
              itemCount={entities.length}
              hasZebraStriping
              selectable={true}
              selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
              onSelectionChange={handleSelectionChange}
              promotedBulkActions={[
                {
                  content: 'Ký phát hành',
                  icon: BillFilledIcon,
                  onAction: signedAllSelected,
                  disabled: selectedResources.length < 1,
                },
                {
                  content: 'Xoá',
                  icon: DeleteIcon,
                  onAction: deleteAllSelected,
                  disabled: selectedResources.length < 1,
                },
              ]}
              emptyState={
                <Text as="p" tone="subdued">
                  {loading ? 'Đang tải dữ liệu...' : 'Không có hóa đơn nháp.'}
                </Text>
              }
              headings={[
                { title: 'STT' },
                { title: 'Mã tra cứu' },
                { title: 'Mã tham chiếu' },
                { title: 'Biển số' },
                { title: 'Chuyến' },
                { title: 'Khởi hành' },
                { title: 'Loại thuế' },
                { title: 'Tên người mua' },
                { title: 'CCCD' },
                { title: 'Tên đơn vị' },
                { title: 'MST' },
                { title: 'SĐT' },
                { title: 'Email' },
                { title: 'Địa chỉ' },
                { title: 'Thuế', alignment: 'end' },
                { title: 'Thành tiền', alignment: 'end' },
                { title: 'Tổng tiền', alignment: 'end' },
                { title: 'Ngày tạo' },
                { title: 'Người tạo' },
                { title: '--' },
              ]}
            >
              {rowMarkup}
            </IndexTable>
          </div>
        </Card>
      </Box>
      {totalItems > 0 ? (
        <Pagination
          TotalRecord={totalItems}
          onChange={onChangePagination}
          pageSize={Number(mainQuery.limit)}
          activeCurrentPage={Number(mainQuery.page)}
        />
      ) : null}
    </BlockStack>
  );
}

